import { FC } from 'react';
import { Autocomplete, Divider, TextField } from '@mui/material';
import { sortBy } from 'lodash';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { currencyLabels, majorCurrencies } from '../../labels';

type Props = {
  error?: {
    message?: string;
  };
  placeholder?: string;
  value?: CurrencyCode;
  onChange: (value: CurrencyCode) => void;
  name?: string;
  disabled?: boolean;
  fullWidth?: boolean;
};

const majorCurrencyOptions = majorCurrencies.map((currency) => ({
  label: currencyLabels[currency],
  value: currency,
}));

const nonMajorCurrencies = Object.values(CurrencyCode).filter(
  (currency) => !majorCurrencies.includes(currency),
);

const nonMajorCurrencyOptions = sortBy(
  nonMajorCurrencies.map((currency) => ({
    label: currencyLabels[currency],
    value: currency,
  })),
  'label',
);

const allOptions = [...majorCurrencyOptions, ...nonMajorCurrencyOptions];

const CurrencySelector: FC<Props> = ({
  fullWidth,
  name,
  disabled,
  error,
  placeholder,
  value = '',
  onChange,
}) => {
  return (
    <Autocomplete
      fullWidth={fullWidth}
      disableClearable
      disabled={disabled}
      options={allOptions}
      placeholder={placeholder}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      groupBy={(option) =>
        nonMajorCurrencies.includes(option.value as CurrencyCode)
          ? 'Other'
          : 'Major'
      }
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group === 'Other' && params.key !== 0 && (
            <Divider sx={{ my: 2 }} />
          )}
          <ul>{params.children}</ul>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="small"
          name={name}
          error={!!error}
          helperText={error?.message?.toString()}
          type="text"
          placeholder={placeholder}
        />
      )}
      value={
        value
          ? { value, label: currencyLabels[value as CurrencyCode] }
          : undefined
      }
      onChange={(_, newValue) => {
        onChange(newValue.value as CurrencyCode);
      }}
    />
  );
};

export default CurrencySelector;
